import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { every, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const SALES_GAME_FIELDS = {
  name: "",
  description: "",
  theme: "mountain", // mountain, space
  rankBy: "margin", // margin, sales, brand
  rankByBrand: null, // Brand name when rankBy is 'brand'
  goalType: "individual", // individual, company
  goalAmount: null, // Target amount for the summit/finish, null = unlimited ("get the most")
  unlimitedGoal: false, // true = no target, just compete for highest
  startDate: null,
  endDate: null,
  active: false,

  // Eligibility filters
  participants: [], // Array of user IDs who can participate (empty = all eligible)
  locations: [], // Array of location values (empty = all locations)

  // Prize info
  prizeTitle: "",
  prizeDescription: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SALES_GAME_FIELDS);

export const SALES_GAME_THEMES = {
  mountain: {
    label: "Mountain Climb",
    icon: "terrain",
  },
  space: {
    label: "Space Race",
    icon: "rocket",
  },
};
deepFreeze(SALES_GAME_THEMES);

export const SALES_GAME_RANK_BY = {
  margin: {
    label: "Margin",
  },
  sales: {
    label: "Total Sales (Pretax)",
  },
  brand: {
    label: "Sales by Brand",
  },
};
deepFreeze(SALES_GAME_RANK_BY);

export const SALES_GAME_GOAL_TYPES = {
  individual: {
    label: "Individual",
  },
  company: {
    label: "Company",
  },
};
deepFreeze(SALES_GAME_GOAL_TYPES);

export default class SalesGame extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, SALES_GAME_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.startDate = timestampToDate(data.startDate);
    data.endDate = timestampToDate(data.endDate);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: SalesGame.canBeViewedBy,
      create: SalesGame.canBeCreatedBy,
      edit: SalesGame.canBeEditedBy,
      delete: SalesGame.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user, doc) {
    if (!user.isEmployee) return false;
    // If no participants specified, all employees can view
    if (!doc || !doc.data.participants || doc.data.participants.length === 0) {
      return true;
    }
    // Otherwise, only participants can view
    return doc.data.participants.includes(user.id);
  }

  static canBeCreatedBy(user) {
    return user.isAdmin;
  }

  static canBeEditedBy(user) {
    return user.isAdmin;
  }

  static canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return SalesGame.getPath(this.id);
  }

  static getPath(id) {
    if (id) {
      return `salesGames/${id}`;
    }
    return "salesGames";
  }

  static getStateID(path) {
    const match = path.match(/^salesGames\/([^/]+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  get stateID() {
    return this.id;
  }

  // Helpers

  get isActive() {
    if (!this.data.active) return false;
    const now = new Date();
    if (this.data.startDate && now < this.data.startDate) return false;
    if (this.data.endDate && now > this.data.endDate) return false;
    return true;
  }

  get themeInfo() {
    return SALES_GAME_THEMES[this.data.theme] || SALES_GAME_THEMES.mountain;
  }

  get rankByInfo() {
    return SALES_GAME_RANK_BY[this.data.rankBy] || SALES_GAME_RANK_BY.margin;
  }

  get hasPrize() {
    return Boolean(this.data.prizeTitle);
  }

  get hasUnlimitedGoal() {
    return this.data.unlimitedGoal || this.data.goalAmount === null;
  }

  isParticipant(user) {
    // If no participants specified, all employees are participants
    if (!this.data.participants || this.data.participants.length === 0) {
      // If locations are specified, check if user is in one of those locations
      if (this.data.locations && this.data.locations.length > 0) {
        const userLocations = user.data?.locations || [];
        return this.data.locations.some((loc) => userLocations.includes(loc));
      }
      return user.isEmployee;
    }
    return this.data.participants.includes(user.id);
  }

  // Editing

  get rules() {
    return {
      name: [(a) => Boolean(a && a.trim()) || "required"],
      theme: [
        (a) => Boolean(a) || "required",
        (t) => t in SALES_GAME_THEMES || "themeInvalid",
      ],
      rankBy: [
        (a) => Boolean(a) || "required",
        (r) => r in SALES_GAME_RANK_BY || "rankByInvalid",
      ],
      goalType: [
        (a) => Boolean(a) || "required",
        (g) => g in SALES_GAME_GOAL_TYPES || "goalTypeInvalid",
      ],
      startDate: [(a) => Boolean(a) || "required"],
      endDate: [(a) => Boolean(a) || "required"],
      goalAmount: [
        (a) => this.data.unlimitedGoal || (a !== null && a > 0) || "required",
      ],
      participants: [
        (a) => (Array.isArray(a) && a.length >= 2) || "participantsMinTwo",
      ],
      prizeTitle: [(a) => Boolean(a && a.trim()) || "required"],
      prizeDescription: [(a) => Boolean(a && a.trim()) || "required"],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, SALES_GAME_FIELDS)));
  }

  isEqual(game) {
    return Boolean(this.id === game.id && isEmpty(diff(this.data, game.data)));
  }

  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new SalesGame(snapshot.id, data);
  }
}
